import React, { useCallback, useEffect, useRef, useState } from "react";

import PageLayout from "../../components/PageLayout";
import ActionLoader from "../../components/ActionLoader";
import Toast from "../../components/Toast";

import { GET_CUSTOM_HEDGE_REPORT_URL, getData } from "../../utils/api";
import { getLoginDetails, canAccessCustomHedgeReport } from "../../utils/storage";

import InsightsHeaderIcon from "../../icons/newicons/insights.svg";

import "./insights.css";
import "./converter-testing.css";
import { InsightsSubNav } from "./InsightsSubNav";

const PAGE_SIZE = 10;

function formatNumber(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** Net custom hedge after global discount: custom - (custom * |disc| / 100) */
function applyDisc(customValue, discPercent) {
  const custom = Number(customValue);
  const disc = Number(discPercent);
  if (Number.isNaN(custom)) return null;
  if (Number.isNaN(disc) || disc === 0) return custom;
  return custom - (custom * Math.abs(disc)) / 100;
}

function paginationLabel(offset, pageSize, totalCount, pageLength) {
  if (!totalCount) return "No records";
  const from = totalCount === 0 ? 0 : offset + 1;
  const to = offset + pageLength;
  return `Showing ${from}–${to} of ${totalCount.toLocaleString()}`;
}

function parseReportResponse(res) {
  const inner = res?.data != null ? res.data : res;
  return {
    list: inner?.list || [],
    totalCount: inner?.totalCount ?? 0,
    areMoreItemsAvailable: Boolean(inner?.areMoreItemsAvailable),
    ptDisc: inner?.ptDisc ?? 0,
    pdDisc: inner?.pdDisc ?? 0,
    rhDisc: inner?.rhDisc ?? 0,
  };
}

function mapListItems(list, discs) {
  const { ptDisc = 0, pdDisc = 0, rhDisc = 0 } = discs || {};
  return list.map((item) => ({
    ...item,
    companyName: item.companyName || "—",
    firstName: item.firstName || "—",
    lastName: item.lastName || "—",
    customPt: formatNumber(applyDisc(item.customPt, ptDisc)),
    customPd: formatNumber(applyDisc(item.customPd, pdDisc)),
    customRh: formatNumber(applyDisc(item.customRh, rhDisc)),
    customCount: formatNumber(item.customCount),
    itemsUtilised: formatNumber(item.itemsUtilised),
  }));
}

const CustomHedge = () => {
  const canViewCustomHedge = canAccessCustomHedgeReport(getLoginDetails());

  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [areMoreItemsAvailable, setAreMoreItemsAvailable] = useState(false);
  const [discs, setDiscs] = useState({ ptDisc: 0, pdDisc: 0, rhDisc: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [toastParams, setToastParams] = useState({ message: "", type: "", id: 0 });

  const fetchTokenRef = useRef(0);

  const fetchReport = useCallback(async (text, pageOffset) => {
    const token = ++fetchTokenRef.current;
    try {
      setIsLoading(true);
      const loginDetails = getLoginDetails() || {};
      const params = {
        count: PAGE_SIZE,
        offset: pageOffset,
        parentCompanyId: loginDetails.parentCompanyId,
      };
      if (text) params.text = text;

      const res = await getData(GET_CUSTOM_HEDGE_REPORT_URL, params);
      if (token !== fetchTokenRef.current) return;

      const parsed = parseReportResponse(res);
      setList(parsed.list);
      setTotalCount(parsed.totalCount);
      setAreMoreItemsAvailable(parsed.areMoreItemsAvailable);
      setDiscs({
        ptDisc: parsed.ptDisc,
        pdDisc: parsed.pdDisc,
        rhDisc: parsed.rhDisc,
      });
    } catch (err) {
      if (token !== fetchTokenRef.current) return;
      console.log("Custom hedge report error", err);
      setList([]);
      setTotalCount(0);
      setAreMoreItemsAvailable(false);
      setDiscs({ ptDisc: 0, pdDisc: 0, rhDisc: 0 });
      setToastParams({
        message: "Failed to load custom hedge report.",
        type: "error",
        id: Date.now(),
      });
    } finally {
      if (token === fetchTokenRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!canViewCustomHedge) return;
    fetchReport(appliedSearch, offset);
  }, [appliedSearch, offset, fetchReport, canViewCustomHedge]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedSearch(searchText.trim());
    setOffset(0);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setAppliedSearch("");
    setOffset(0);
  };

  const tableItems = mapListItems(list, discs);

  if (!canViewCustomHedge) {
    return (
      <PageLayout heading="Insights" hideSearchBar icon={InsightsHeaderIcon}>
        <InsightsSubNav />
        <div className="c-gray fs-16">You do not have permission to view the custom hedge report.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout heading="Insights" hideSearchBar icon={InsightsHeaderIcon}>
      <InsightsSubNav />

      <form className="converter-testing-filters" onSubmit={handleApplyFilters}>
        <div className="converter-testing-filter-field converter-testing-filter-search">
          <label htmlFor="custom-hedge-search" className="converter-testing-filter-sr">
            Search
          </label>
          <input
            id="custom-hedge-search"
            type="text"
            value={searchText}
            placeholder="Search company / name..."
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="converter-testing-filter-actions">
          <button type="submit" className="converter-testing-filter-btn converter-testing-filter-btn-apply">
            Apply
          </button>
          <button
            type="button"
            className="converter-testing-filter-btn converter-testing-filter-btn-clear"
            onClick={handleClearFilters}
          >
            Clear
          </button>
        </div>
      </form>

      {isLoading && <ActionLoader />}

      {!isLoading && (
        <>
          <div className="converter-testing-details-meta">
            {paginationLabel(offset, PAGE_SIZE, totalCount, tableItems.length)}
          </div>
          {tableItems.length === 0 ? (
            <div className="c-gray fs-14">No entities with custom hedge match your search.</div>
          ) : (
            <>
              <div className="converter-testing-drift-wrap">
                <table className="converter-testing-drift-table converter-testing-custom-hedge-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Custom Pt</th>
                      <th>Custom Pd</th>
                      <th>Custom Rh</th>
                      <th>Custom Count</th>
                      <th>Items Utilised</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableItems.map((item, index) => (
                      <tr key={item.companyId || `${item.companyName}-${index}`}>
                        <td>{item.companyName}</td>
                        <td>{item.firstName}</td>
                        <td>{item.lastName}</td>
                        <td>{item.customPt}</td>
                        <td>{item.customPd}</td>
                        <td>{item.customRh}</td>
                        <td>{item.customCount}</td>
                        <td>{item.itemsUtilised}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="converter-testing-pagination">
                <button
                  type="button"
                  className="converter-testing-page-btn converter-testing-page-btn-prev"
                  disabled={offset === 0}
                  onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="converter-testing-page-btn converter-testing-page-btn-next"
                  disabled={!areMoreItemsAvailable}
                  onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </>
      )}

      <Toast
        message={toastParams.message}
        type={toastParams.type}
        id={toastParams.id}
      />
    </PageLayout>
  );
};

export default CustomHedge;
